import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import { LabelValue } from '~/components/common/labels/LabelValue'
import {
  calculatePackageCount,
  calculateTotalUnits,
} from '~/components/dashboard/admin/catalog/inventory/inventoryUtils'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'
import { getInventoryItemDisplayQuantity } from '~/utils/inventoryItemUtils'

import styles from './OrderItemReceiptsSummary.module.scss'

interface OrderItemReceiptsSummaryProps {
  inventoryOrderItem: InventoryOrderItem
  totalQuantityReceived: number
  totalQuantityRejected: number
}

export const OrderItemReceiptsSummary = ({
  inventoryOrderItem,
  totalQuantityReceived,
  totalQuantityRejected,
}: OrderItemReceiptsSummaryProps) => {
  const { t } = useTranslation('Admin')

  const { orderedQuantity, perPackageAmount, packageUnits, packageType } =
    inventoryOrderItem

  const PackageType = useSelector(getPackageType)
  const ProductSizeUnit = useSelector(getProductSizeUnit)

  const productUnit = LanguageUtils.getConstantTranslatedName(
    packageUnits.id,
    ProductSizeUnit,
  )
  const packageTypeName = LanguageUtils.getConstantTranslatedName(
    packageType.id,
    PackageType,
  )

  const calculateRemainingQuantity = () => {
    const totalQuantity = totalQuantityReceived + totalQuantityRejected
    const availableQuantity = calculateTotalUnits(
      orderedQuantity,
      perPackageAmount,
    )
    return Math.max(0, availableQuantity - totalQuantity)
  }

  const formatQuantityDetail = (quantity: number) => {
    const packageQuantity = calculatePackageCount(quantity, perPackageAmount)
    const { total, breakdown } = getInventoryItemDisplayQuantity({
      orderedQuantity: packageQuantity,
      perPackageAmount,
      productSizeUnit: productUnit,
      packageType: packageTypeName,
      originalQuantity: quantity,
      breakdownStyle: 'simplified',
    })

    return (
      <>
        <span>{total}</span>
        {breakdown && <> ({breakdown})</>}
      </>
    )
  }

  const remainingQuantity = calculateRemainingQuantity()
  return (
    <div className={styles.labels}>
      <LabelValue
        ValueProps={{ className: styles.receivingLabel }}
        label={t('Admin:CATALOG.ORDERS.RECEIVING_LABEL')}
        layout="vertical"
        value={formatQuantityDetail(totalQuantityReceived)}
      />
      <LabelValue
        ValueProps={{ className: styles.rejectedLabel }}
        label={t('Admin:CATALOG.ORDERS.REJECTED_LABEL')}
        layout="vertical"
        value={formatQuantityDetail(totalQuantityRejected)}
      />
      <LabelValue
        ValueProps={{ className: styles.remainingLabel }}
        label={t('Admin:CATALOG.ORDERS.REMAINING_LABEL')}
        layout="vertical"
        value={
          remainingQuantity === 0
            ? t('Common:NONE').toLowerCase()
            : formatQuantityDetail(remainingQuantity)
        }
      />
    </div>
  )
}
